import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  Button,
  Alert,
  StyleSheet,
  Platform,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import uuid from "react-native-uuid";
import { WorthApi } from "../api/WorthApi";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { RootStackParamList } from "../routes";

const STORAGE_KEY = "vesting:grants";

type Props = NativeStackScreenProps<RootStackParamList, "VestingAdd">;

export default function VestingAdd({ navigation }: Props) {
  const [dateIn, setDateIn] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  const [sharesIn, setSharesIn] = useState<string>("100");
  const [symbolIn, setSymbolIn] = useState<string>("");
  const [deferralIn, setDeferralIn] = useState<string>("3");

  function parseDateIso(s: string) {
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return d;
  }

  async function saveLocal(list: any[]) {
    try {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      console.warn("Failed to save grants to storage", e);
    }
  }

  async function addGrant() {
    const d = parseDateIso(dateIn);
    if (!d) return;
    if (!symbolIn || symbolIn.trim() === "") {
      Alert.alert("Validation", "Symbol is required");
      return;
    }
    const sharesNum = Number(sharesIn);
    if (!Number.isFinite(sharesNum) || sharesNum <= 0) {
      Alert.alert("Validation", "Shares must be a number greater than 0");
      return;
    }

    const payload = {
      grantDate: d.toISOString().slice(0, 10),
      shares: sharesNum,
      symbol: symbolIn,
      deferralYears: Number(deferralIn) || 3,
      vestedOverYears: Number(deferralIn) || 3,
      notes: undefined,
    };

    try {
      const res = await WorthApi.post("/vesting/grants", payload);
      // nothing else needed — main list will reload on focus
    } catch (e) {
      console.warn("Failed to save grant to API, saving locally", e);
      try {
        const raw = await AsyncStorage.getItem(STORAGE_KEY);
        const existing = raw ? JSON.parse(raw) : [];
        const g = {
          id: String(uuid.v4()),
          grantDate: payload.grantDate,
          shares: payload.shares,
          symbol: payload.symbol,
          deferralYears: payload.deferralYears,
          vestedOverYears: payload.vestedOverYears,
        };
        existing.unshift(g);
        await saveLocal(existing);
      } catch (err) {
        console.warn("Failed to persist locally", err);
      }
    }

    navigation.goBack();
  }

  return (
    <View
      style={[
        styles.container,
        Platform.OS === "web" ? { minHeight: "100vh" } : {},
      ]}
    >
      <Text style={styles.title}>Add Vesting Grant</Text>

      <Text style={styles.label}>Grant date (YYYY-MM-DD)</Text>
      <TextInput style={styles.input} value={dateIn} onChangeText={setDateIn} />

      <Text style={styles.label}>Symbol</Text>
      <TextInput
        style={styles.input}
        value={symbolIn}
        onChangeText={setSymbolIn}
      />

      <Text style={styles.label}>Shares</Text>
      <TextInput
        style={styles.input}
        value={sharesIn}
        onChangeText={setSharesIn}
        keyboardType="numeric"
      />

      <Text style={styles.label}>Vested over (years)</Text>
      <TextInput
        style={styles.input}
        value={deferralIn}
        onChangeText={setDeferralIn}
        keyboardType="numeric"
      />

      <Button title="Save grant" onPress={addGrant} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 12, backgroundColor: "#201D2B" },
  title: { fontSize: 20, color: "white", marginBottom: 12 },
  input: {
    borderWidth: 1,
    borderColor: "#444",
    padding: 8,
    color: "white",
    marginBottom: 8,
  },
  label: { color: "white", marginBottom: 4 },
});
